import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// Capture a DOM node and save it as a clean, single-page A4 PDF (scaled to fit).
export async function exportNodeToPdf(node, filename, bg = "#F7F5F0") {
  if (!node) return;
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: bg,
    useCORS: true,
    logging: false,
    windowWidth: node.scrollWidth,
  });
  const imgData = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const ratio = Math.min((pageW - margin * 2) / canvas.width, (pageH - margin * 2) / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  const x = (pageW - w) / 2;
  pdf.addImage(imgData, "JPEG", x, margin, w, h);
  pdf.save(filename);
}

const PDF_W = 880;
const ATOMIC = ".break-inside-avoid, [data-pdf-visual], figure, table, tr, li, p, h1, h2, h3, h4, svg, img, .recharts-wrapper, [class*='grid-cols']";

const waitImages = (root) => Promise.all([...root.querySelectorAll("img")].map((img) => {
  img.loading = "eager";
  return Promise.race([img.decode().catch(() => {}), new Promise((r) => setTimeout(r, 10000))]);
}));

// Lay the report out at a fixed width, shrink oversized visuals/tables, then record unbreakable blocks.
function prepare(root, pageCssH) {
  root.querySelectorAll(".report-drill, [data-drill-cue], [data-pdf-hide]").forEach((el) => { el.style.display = "none"; });
  root.querySelectorAll("[data-pdf-only]").forEach((el) => { el.style.display = "inline-flex"; });
  root.querySelectorAll("[data-testid='report-attachments'] > .grid").forEach((g) => {
    if ([...g.children].filter((c) => c.style.display !== "none").length < 2) g.style.gridTemplateColumns = "1fr";
  });
  root.querySelectorAll("[data-pdf-visual]").forEach((el) => {
    const h = el.getBoundingClientRect().height;
    const limit = pageCssH * 0.8;
    if (h > limit) { el.style.width = `${(limit / h) * 100}%`; el.style.marginLeft = "auto"; el.style.marginRight = "auto"; }
  });
  root.querySelectorAll("table").forEach((t) => {
    const box = t.parentElement;
    const avail = box.clientWidth;
    if (t.scrollWidth <= avail + 1) return;
    const f = avail / t.scrollWidth;
    const h = t.getBoundingClientRect().height;
    box.style.overflow = "hidden";
    box.style.height = `${h * f}px`;
    t.style.width = "max-content";
    t.style.transformOrigin = "top left";
    t.style.transform = `scale(${f})`;
  });
  const top = root.getBoundingClientRect().top;
  const guards = [];
  root.querySelectorAll(ATOMIC).forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0) guards.push({ top: r.top - top, bottom: r.bottom - top });
  });
  // Keep each project/goal/expansion item together with its attachments when it fits on a page.
  root.querySelectorAll("[data-testid='report-attachments']").forEach((el) => {
    const r = el.parentElement.getBoundingClientRect();
    guards.push({ top: r.top - top, bottom: r.bottom - top });
  });
  // Keep headings/labels with the start of what follows them.
  root.querySelectorAll("h2, h3, [class*='uppercase'][class*='tracking']").forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.height < 60) guards.push({ top: r.top - top, bottom: r.bottom - top + 140 });
  });
  return guards;
}

// Pick the lowest cut <= target that doesn't slice any block shorter than a page.
function findCut(guards, start, target, pageH) {
  let cut = target;
  for (let n = 0; n < 300; n += 1) {
    const g = guards.find((x) => x.top < cut - 1 && x.bottom > cut + 1 && x.bottom - x.top < pageH * 0.98 && x.top > start + 1);
    if (!g) return cut;
    cut = g.top - 6;
  }
  return cut;
}

// Capture a tall DOM node and paginate it across A4 pages without slicing tables, images, cards or text.
export async function exportNodeToPdfPaged(node, filename, bg = "#F7F5F0") {
  if (!node) return;
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 28;
  const contentW = pageW - margin * 2;
  const pageCssH = PDF_W * ((pageH - margin * 2) / contentW);

  const host = document.createElement("div");
  host.style.cssText = `position:absolute;left:-20000px;top:0;width:${PDF_W}px;pointer-events:none;`;
  const clone = node.cloneNode(true);
  clone.style.width = `${PDF_W}px`;
  clone.style.maxWidth = `${PDF_W}px`;
  clone.style.margin = "0";
  host.appendChild(clone);
  document.body.appendChild(host);
  let canvas;
  let guards;
  try {
    await waitImages(clone);
    await new Promise((r) => requestAnimationFrame(() => r()));
    guards = prepare(clone, pageCssH);
    canvas = await html2canvas(clone, { scale: 2, backgroundColor: bg, useCORS: true, logging: false, windowWidth: 1280 });
  } finally { host.remove(); }

  const k = canvas.width / PDF_W;
  const totalCss = canvas.height / k;
  const ptPerCss = contentW / PDF_W;
  let start = 0;
  let page = 0;
  while (start < totalCss - 2 && page < 60) {
    const target = start + pageCssH;
    let end = target >= totalCss ? totalCss : findCut(guards, start, target, pageCssH);
    if (end - start < pageCssH * 0.25) end = Math.min(target, totalCss);
    const hPx = Math.round((end - start) * k);
    const pc = document.createElement("canvas");
    pc.width = canvas.width;
    pc.height = hPx;
    const ctx = pc.getContext("2d");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, pc.width, pc.height);
    ctx.drawImage(canvas, 0, Math.round(start * k), canvas.width, hPx, 0, 0, canvas.width, hPx);
    if (page > 0) pdf.addPage();
    pdf.setFillColor(bg);
    pdf.rect(0, 0, pageW, pageH, "F");
    pdf.addImage(pc.toDataURL("image/jpeg", 0.92), "JPEG", margin, margin, contentW, (end - start) * ptPerCss);
    start = end;
    page += 1;
  }
  pdf.save(filename);
}
